/**
 * `GET /v1/departments?editionId=…` — one årgang's own tree, which is not the organisation's.
 *
 * ---------------------------------------------------------------------------------------------
 * THE PARAMETER IS THE WHOLE FILE. `listDepartmentsQuerySchema.editionId` says it out loud:
 * "ABSENT MEANS THE ORGANISATION'S OWN, WHICH IS WHAT SETTINGS ADMINISTERS." The `departments`
 * table holds both kinds of row at once — the template (`edition_id is null`) and each edition's
 * copy of it, linked by `key` — and until this parameter existed the list could only ever answer
 * with the template. That is why `POST /v1/editions/:id/clone` could write nine departments into
 * Nordlys 2027 and no surface in the product could show them: the receipt was the only evidence
 * the clone had happened.
 *
 * SO SENDING IT IS THE POINT, AND FORGETTING IT IS THE BUG. A page that dropped `editionId`
 * would not fail. It would render — nine rows, the right shape, the right two levels — showing
 * the ORGANISATION'S groups under an årgang's name, and nothing on screen would say so. That is
 * the single most confusing defect this page can have, and it is why the request is asserted in
 * the test rather than only the rows that come back from it.
 *
 * NOT A SECOND COPY OF `departmentsQueryOptions`. That one (in `features/tasks/queries.ts`,
 * re-exported by `features/settings/department-queries.ts`) describes the TEMPLATE list, and
 * CLAUDE.md's one-place rule is about two descriptions of ONE cache entry. These are two entries
 * by construction — `qk.departmentList` and `qk.editionDepartmentList` branch apart under
 * `qk.departments(orgId)`, deliberately, so neither can be served out of the other — and merging
 * them into one options function with an optional argument would put the difference that matters
 * behind an `undefined` nobody reads. The sort, the page size and the tier are the same because
 * the endpoint and the shape of the answer are the same.
 *
 * REFERENCE TIER, matching the template list for the reason that one gives: an årgang's groups
 * change a few times a year, and the clone that creates them invalidates `qk.departments(orgId)`
 * — which this key sits under — so a copy made in another tab lands here without a poll.
 * ---------------------------------------------------------------------------------------------
 */
import {
  departmentListResponseSchema,
  MAX_PAGE_SIZE,
  type DepartmentListResponse,
} from '@tvitvi/shared';
import { queryOptions } from '@tanstack/react-query';

import { CACHE } from '@/api/cache-tiers';
import { apiRequest } from '@/api/client';
import { qk } from '@/api/query-keys';

/**
 * Every department and section this årgang has of its own, in the order the årgang gave them.
 *
 * One page. `MAX_PAGE_SIZE` is 100 and the tree is two levels deep over an organisation's own
 * groups — the seed writes nine — so a second page is not a shape this data produces. If one
 * ever did, `departmentLevels` already carries the sections whose parent did not fit rather than
 * dropping them, which is the failure mode that would matter.
 */
export function editionDepartmentsQueryOptions(orgId: string, editionId: string) {
  return queryOptions({
    queryKey: qk.editionDepartmentList(orgId, editionId),
    queryFn: ({ signal }): Promise<DepartmentListResponse> =>
      apiRequest({
        path: '/departments',
        query: { editionId, sort: 'order', direction: 'asc', limit: MAX_PAGE_SIZE },
        schema: departmentListResponseSchema,
        signal,
      }),
    ...CACHE.reference,
  });
}
